import { Stack, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

export const Container = styled(Box)(({ theme }) => ({
  padding: `0 ${theme.spacing(2.5)}`,
  backgroundColor: 'white',
  margin: '0 auto',
  color: 'black',
  '@media print': {
    padding: `0 ${theme.spacing(0.75)}`,
  },
}));

export const PageBreak = styled(Box)(({ theme }) => ({
  display: 'none',
  '@media print': {
    marginTop: '3rem',
    display: 'block',
    pageBreakBefore: 'always',
  },
}));

export const Page = styled(Box)(({ theme }) => ({
  paddingTop: '3rem',

  '@media print': {
    paddingTop: 0,
    fontSize: '1rem',
  },
}));

type HeaderProps = {
  hidden?: boolean;
};
export const Header = styled(Box, { shouldForwardProp: (prop) => prop !== 'hidden' })<HeaderProps>(
  ({ theme, hidden }) => ({
    display: hidden ? 'none' : 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem',
    '@media print': {
      display: 'flex',
    },
  }),
);

export const Logo = styled(Box)(({ theme }) => ({
  width: '200px',
  margin: theme.spacing(2),
  '> img': {
    width: '100%',
  },
}));

export const PatientDetails = styled(Box)(({ theme }) => ({
  display: 'flex',
  padding: theme.spacing(2),
  margin: theme.spacing(1),
  flexGrow: 1,
  border: '1px #dde2e9 solid',
  borderRadius: '0.6rem',
  flexDirection: 'row',
  justifyContent: 'flex-start',
  alignItems: 'center',
  color: 'inherit',
}));

export const HeaderTextContainer = styled(Box)(({ theme }) => ({
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
}));
export const HeaderLabel = styled(Typography)(({ theme }) => ({
  marginBottom: '0.5rem',
  marginTop: '0.5rem',
  fontSize: '0.9rem',
  color: 'inherit',
  '@media print': {
    fontSize: '0.8rem',
  },
}));
export const HeaderText = styled(Typography)(({ theme }) => ({
  marginBottom: '0.5rem',
  marginTop: '0.5rem',
  fontSize: '1rem',
  color: 'inherit',
  '@media print': {
    fontSize: '0.9rem',
  },
}));

export const DayContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  border: '1px #dde2e9 solid',
  margin: 0,
  padding: '0.5rem 1rem',
  borderRadius: '0.6rem',
}));
export const DayTitle = styled(Typography)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '1rem',
  fontWeight: 700,
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  svg: {
    marginRight: theme.spacing(1),
  },
}));

export const OutlinedBox = styled(Box)(({ theme }) => ({
  padding: `${theme.spacing(1)} ${theme.spacing(2)} `,
  // marginBottom: theme.spacing(1),
  backgroundColor: '#fafbfc',
  display: 'flex',
  flexGrow: 1,
  maxWidth: '50%',
  justifyContent: 'space-between',
  border: '1px #dde2e9 solid',
  borderRadius: '0.6rem',
  fontSize: '1rem',
  alignItems: 'center',
  color: 'inherit',
}));

type CircleProps = {
  color: string;
  small?: boolean;
};
export const Circle = styled('div')<CircleProps>(({ theme, color, small }) => ({
  width: small ? '0.8rem' : '1.2rem',
  height: small ? '0.8rem' : '1.2rem',
  borderRadius: '100%',
  display: 'block',
  marginRight: small ? theme.spacing(0.3) : '1rem',
  backgroundColor: color,
}));

export const AverageReportContainer = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '2fr 1.5fr',
}));

export const AverageItemsStack = styled(Stack)(({ theme }) => ({
  backgroundColor: '#fafbfc',
  border: '1px #dde2e9 solid',
  borderRadius: '0.6rem',
  width: '100%',
  height: 'auto',
}));

export const AverageItem = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr 80px 80px',
  alignItems: 'center',
  borderBottom: '1px #dde2e9 solid',
  padding: `${theme.spacing(1)} ${theme.spacing(2)}`,
  '&:last-child': {
    borderBottom: 'none',
  },
}));

export const PieChartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 2rem',
}));
export const TimelineContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  flexDirection: 'column',
  marginTop: theme.spacing(3),
}));
export const TimelineItem = styled(Box)(({ theme }) => ({
  margin: `${theme.spacing(0.5)} 0`,
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
}));

export const TimelineBar = styled(Box)(({ theme }) => ({
  height: '100%',
  width: '100%',
  borderRadius: '0.6rem',
  display: 'flex',
  position: 'relative',
  div: {
    fontSize: '0.6rem',
  },
  marginLeft: theme.spacing(1),
  '@media print': {
    '> div': {
      fontSize: '0.5rem',
      height: '0.8rem !important',
    },
  },
}));
export const TimelineText = styled(Typography)(({ theme }) => ({
  fontSize: '0.8rem',
  whiteSpace: 'nowrap',
  width: '40px',
  '@media print': {
    fontSize: '0.6rem',
  },
}));
type SeparatorProps = {
  percentage: 25 | 50 | 75;
};
export const Separator = styled('div')<SeparatorProps>(({ theme, percentage }) => ({
  position: 'absolute',
  height: 'calc(100% + 1rem)',
  width: 0,
  borderLeft: '1px dashed #8f9edd',
  borderRight: '1px dashed #8f9edd',
  left: `calc(${percentage}%)`,
  transform: 'translateX(50%)',
  top: '-0.5rem',
  bottom: '0.5rem',
}));

export const Table = styled('div')(({ theme }) => ({
  display: 'table',
  width: '100%',
  margin: 0,
}));

export const Row = styled('div')(({ theme }) => ({
  display: 'table-row',
  background: '#ffffff',
}));
export const HeaderRow = styled(Row)(({ theme }) => ({
  color: '#fff !important',
  background: '#2f3659',
  fontSize: '13px',
}));

type CellProps = {
  header?: boolean;
};
export const Cell = styled('div')<CellProps>(({ theme, header }) => ({
  display: 'table-cell',
  color: header ? '#fff' : '#666',
  lineHeight: 1.2,
  fontWeight: 'unset !important',
  paddingTop: '16px',
  paddingBottom: '16px',
  borderBottom: '1px solid #f2f2f2',
  fontSize: header ? '13px' : '11px',
  ...(header && { fontWeight: 600 }),
  ':nth-child(1)': {
    width: '100px',
    paddingLeft: theme.spacing(2),
    fontWeight: '600 !important',
  },
}));
export const CellContent = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
}));
